// src/components/StudentUpcomingMeetingsModal.js
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "react-i18next";

const ensureFullUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http://') || url.startsWith('https://')
    ? url
    : `https://${url}`;
};

// Format "YYYY/MM/DD" -> "6 October"
const formatDateHuman = (dateStr, t) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('/').map(Number);

  const months = [
    t("January"), t("February"), t("March"), t("April"), t("May"), t("June"),
    t("July"), t("August"), t("September"), t("October"), t("November"), t("December"),
  ];

  return `${d} ${months[m - 1] || ''}`;
};

// Group meetings by date
const groupMeetingsByDate = (meetings = [], t) => {
  const groups = {};
  meetings.forEach((m) => {
    const key = m.date || t("Unknown date");
    if (!groups[key]) groups[key] = [];
    groups[key].push(m);
  });
  return groups;
};

function StudentUpcomingMeetingsModal({ open, onClose, meetings = [] }) {
  const { t } = useTranslation();
  const handleJoin = (mtg) => {
    if (!mtg?.url) return;
    const fullUrl = ensureFullUrl(mtg.url);
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  const grouped = groupMeetingsByDate(meetings, t);
  const dateKeys = Object.keys(grouped);
  const colors = ['#635BFF', '#FF8A00', '#FF3B30', '#34C759', '#0A84FF'];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("Your agenda")}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          py: 2.5,
          bgcolor: '#fafbff',
        }}
      >
        {meetings.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" sx={{ color: '#6b7280' }}>
              {t("No upcoming classes at the moment.")}
            </Typography>
          </Box>
        ) : (
          dateKeys.map((dateKey) => (
            <Box key={dateKey} sx={{ mb: 1 }}>
              {/* Date heading */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: '#111827' }}
                >
                  {formatDateHuman(dateKey, t)}
                </Typography>
              </Box>

              {/* Meetings for this date */}
              {grouped[dateKey].map((mtg, idx) => {
                const color = colors[idx % colors.length];
                return (
                  <Box
                    key={mtg.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'stretch',
                      gap: 2,
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: '#ffffff',
                      boxShadow: '0 8px 18px rgba(15, 23, 42, 0.04)',
                    }}
                  >
                    {/* Time block */}
                    <Box sx={{ minWidth: 70 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, color: '#4c1d95', mb: 0.2 }}
                      >
                        {mtg.startTime || '--:--'}
                      </Typography>
                      {mtg.endTime && (
                        <Typography
                          variant="caption"
                          sx={{ color: '#9ca3af' }}
                        >
                          {mtg.endTime}
                        </Typography>
                      )}
                    </Box>

                    {/* Color stripe */}
                    <Box
                      sx={{
                        width: 3,
                        borderRadius: 999,
                        background: color,
                        boxShadow: `0 0 10px ${color}55`,
                      }}
                    />

                    {/* Details + Join */}
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: 0.5,
                      }}
                    >
                      <Chip
                        size="small"
                        label={t("Live session")}
                        sx={{
                          alignSelf: 'flex-start',
                          bgcolor: '#e8f0fe',
                          color: '#2f6fed',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                        }}
                      />

                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: '#111827',
                          mb: 0.2,
                        }}
                      >
                        {mtg.title || t("Untitled session")}
                      </Typography>

                      {mtg.description && (
                        <Typography
                        variant="body2"
                        sx={{
                            color: '#6b7280',
                            whiteSpace: 'normal',    
                            wordBreak: 'break-word', 
                            lineHeight: 1.4,
                            overflowWrap: 'anywhere', 
                        }}
                        >
                        {mtg.description}
                        </Typography>
                      )}

                      <Box
                        sx={{
                          mt: 1,
                          display: 'flex',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleJoin(mtg)}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 999,
                            px: 2.5,
                            py: 0.4,
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            background:
                              'linear-gradient(135deg, #4f46e5, #7c3aed)',
                            boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
                          }}
                        >
                          {t("Join")}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                );
              })}

              {/* Divider between dates */}
              <Divider sx={{ mt: 0.5 }} />
            </Box>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
}

export default StudentUpcomingMeetingsModal;